import {
  ContributionStatus,
  ContributionType,
  TimeLine,
  TimeLineAction,
  User,
} from '../../generated/graphql'

export type SnapshotBump = {
  id: string
  contributionId: string
  userId: string
  reason: string
  value: number
  createdAt: Date
  updatedAt: Date
  username?: string // v1 doesnt have this field
}
export type SnapshotContribution = {
  id: string
  articleId: string
  clusterId: string
  article: {
    id: string
    title: string
    clusterId: string
  }
  bumps: SnapshotBump[]
  type: ContributionType
  reason?: string
  userId: string
  challenge?: string
  motivation?: string
  response?: string
  bumpCount: number
  start: number
  end: number
  originalText: string
  status: ContributionStatus
}
export type SnapshotVote = {
  id: string
  userId: string
  justification: string
  vote: number
  username: string
  articleId: string
  removedAt?: Date
}
export type SnapshotPerspective = {
  id: string
  title: string
  clusterId: string
  cluster: {
    id: string
    topic: string
  }
  nodeId: string
  ownerId: string
  contributions: SnapshotContribution[]
  votes: SnapshotVote[]
  content: string
  votesCount: number
  minCoRank: number
  stats: {
    inquiries: number
    challenges: number
    edits: number
    ownerEdits: number
  }
}
export type SnapshotCluster = {
  id: string
  topic: string
  createdAt: string
  nodeId: string
  perspectives: SnapshotPerspective[]
  stats: {
    perspectives: number
    contributions: number
    votesCount: number
  }
  contentUpdatedAt: Date
}
export type SnapshotNode = {
  id: string
  title: string
  description: string
  createdAt: string
  clusters: SnapshotCluster[]
  stats: {
    clusters: number
  }
}

export type SnapShotProps = {
  node: SnapshotNode
  index: Record<string, number[]>
  tick: number
}

export type SnapshotTreeNode =
  | SnapshotNode
  | SnapshotCluster
  | SnapshotPerspective
  | SnapshotContribution
  | SnapshotBump

export class Snapshot {
  public node: SnapshotNode
  private index: Record<string, number[]>
  public tick: number = -1

  constructor(data: Partial<SnapShotProps> = {}) {
    this.node = data?.node || ({ clusters: [] } as unknown as SnapshotNode)
    this.index = data?.index || {} // uuid to tree position pointer
    this.tick = data?.tick !== undefined ? data.tick : -1
  }

  toObject(): SnapShotProps {
    return {
      node: { ...this.node },
      index: { ...this.index },
      tick: this.tick,
    }
  }

  existNode(uuid: string) {
    return !!this.index[uuid]
  }

  getPath(uuid: string) {
    if (!this.existNode(uuid)) {
      return null
    }
    return this.index[uuid]
  }

  setIndex(uuid: string, path: number[]) {
    // This will be part of the reducer, we can not modify the index, but we need to recreate a new object
    // each time
    this.index = { ...this.index, [uuid]: path }
  }
  getNodeByPath(path: number[]) {
    const treeNode = path.reduce((tree, step, index) => {
      if (!tree) {
        return null
      }
      const treeKeys = [
        'clusters',
        'perspectives',
        'contributions',
        'bumps',
      ] as unknown as (keyof SnapshotTreeNode)[]

      if (!tree[treeKeys[index]]) {
        console.error('Error getting tree node from the snapshot', {
          tree,
          treeKeys,
          index,
          step,
        })
        return null
      }
      return tree[treeKeys[index]][step]
    }, this.node as SnapshotTreeNode) as unknown as SnapshotTreeNode | null
    return treeNode
  }
  getNode<T extends SnapshotTreeNode = SnapshotTreeNode>(
    uuid: string
  ): T | null {
    if (!this.existNode(uuid)) {
      return null
    }
    if (this.index[uuid].length === 1) {
      return this.node as T
    }
    const path = this.index[uuid].slice(1)

    return this.getNodeByPath(path) as T
  }

  updateNode(update: (original: SnapshotNode) => Partial<SnapshotNode>) {
    this.node = { ...this.node, ...update(this.node) }
  }

  updateCluster(
    clusterId: string,
    update: (original: SnapshotCluster) => Partial<SnapshotCluster>
  ) {
    this.updateNode((original) => {
      return {
        clusters: original.clusters.map((cluster) => {
          if (cluster.id === clusterId) {
            return { ...cluster, ...update(cluster) }
          }
          return cluster
        }),
      }
    })
  }

  updatePerspective(
    { clusterId, id }: { clusterId: string; id: string },
    update: (original: SnapshotPerspective) => Partial<SnapshotPerspective>
  ) {
    this.updateCluster(clusterId, (original) => {
      return {
        perspectives: original.perspectives.map((perspective) => {
          if (perspective.id === id) {
            return { ...perspective, ...update(perspective) }
          }
          return perspective
        }),
      }
    })
  }

  updateContribution(
    contribution: SnapshotContribution,
    update: (original: SnapshotContribution) => Partial<SnapshotContribution>
  ) {
    this.updatePerspective(
      {
        clusterId: contribution.clusterId,
        id: contribution.articleId,
      },
      (perspective) => {
        return {
          contributions: perspective.contributions.map((original) => {
            if (original.id === contribution.id) {
              return { ...original, ...update(original) }
            }
            return original
          }),
        }
      }
    )
  }
}

const reduceContributionStats = (
  snapshot: Snapshot,
  contribution: SnapshotContribution
) => {
  const perspectiveId = {
    id: contribution.articleId,
    clusterId: contribution.clusterId,
  }
  const statsKeys: Record<
    Exclude<ContributionType, ContributionType.Reserved>,
    keyof SnapshotPerspective['stats']
  > = {
    [ContributionType.Challenge]: 'challenges',
    [ContributionType.Edit]: 'edits',
    [ContributionType.Inquiry]: 'inquiries',
    [ContributionType.OwnerEdit]: 'ownerEdits',
  }
  snapshot.updatePerspective(perspectiveId, (original) => {
    const key = statsKeys[contribution.type as keyof typeof statsKeys]
    if (!key) {
      return {}
    }

    return {
      stats: {
        ...original.stats,
        [key]: original.stats[key] - 1,
      },
    }
  })

  snapshot.updateCluster(contribution.clusterId, (original) => {
    return {
      stats: {
        ...original.stats,
        contributions: original.stats.contributions - 1,
      },
    }
  })
}
const createContribution = (
  snapshot: Snapshot,
  event: TimeLine,
  type: ContributionType
) => {
  // Target ID is contribution id, but legacy records don't have articleId, then targetId is the perspectiveId
  const perspective = snapshot.getNode<SnapshotPerspective>(
    event.payload.articleId || event.targetId || ''
  )

  if (snapshot.existNode(event.payload.id || '')) {
    return snapshot
  }
  if (!perspective) {
    return snapshot
  }
  const contribution = {
    articleId: perspective.id,
    clusterId: perspective.clusterId,
    article: {
      id: perspective.id,
      title: perspective.title,
      clusterId: perspective.clusterId,
    },
    motivation: event.payload.motivation || '',
    id: event.payload.id || event.payload.contributionId || '',
    title: event.payload.title,
    userId: event.userId,
    reason: event.payload.reason || '',
    type,
    start: +(event.payload.start || 0),
    end: +(event.payload.end || 0),
    originalText: event.payload.originalText || '',
    challenge: event.payload.title || '',
    status:
      type === ContributionType.OwnerEdit
        ? ContributionStatus.Accepted
        : ContributionStatus.Pending,
    bumpCount: 0,
    bumps: [],
  } as SnapshotContribution

  const statsKeys = {
    [ContributionType.Challenge]: 'challenges',
    [ContributionType.Edit]: 'edits',
    [ContributionType.Inquiry]: 'inquiries',
    [ContributionType.OwnerEdit]: 'ownerEdits',
  }
  const statsKey = statsKeys[
    type as keyof typeof statsKeys
  ] as keyof SnapshotPerspective['stats']

  if (type !== ContributionType.OwnerEdit) {
    snapshot.updateCluster(perspective.clusterId, (original) => {
      return {
        stats: {
          ...original.stats,
          contributions: original.stats.contributions + 1,
        },
      }
    })
  }
  snapshot.updatePerspective(perspective, (original) => {
    return {
      contributions: [...original.contributions, contribution],
      stats: {
        ...original.stats,
        [statsKey]: original.stats[statsKey] + 1,
      },
    }
  })

  snapshot.setIndex(contribution.id, [
    ...(snapshot.getPath(perspective.id) || []),
    perspective.contributions.length,
  ])
  return snapshot
}

const createBump = (snapshot: Snapshot, event: TimeLine) => {
  const contribution = snapshot.getNode<SnapshotContribution>(event.targetId)
  if (!contribution) {
    return snapshot
  }

  snapshot.updateContribution(contribution, () => {
    return {
      bumpCount: contribution.bumpCount + 1,
      bumps: [
        ...contribution.bumps,
        {
          id: event.payload.id || '',
          userId: event.userId || '',
          reason: event.title || '',
          value: event.action === TimeLineAction.ContributionBumpedUp ? 1 : -1,
          contributionId: contribution.id,
          createdAt: event.timestamp,
          updatedAt: event.timestamp,
          username: event.payload.username || '',
        },
      ],
    }
  })
  return snapshot
}

const FactoryMethods: Record<
  TimeLineAction,
  (snapshot: Snapshot, event: TimeLine) => Snapshot
> = {
  [TimeLineAction.NodeCreated]: (snapshot: Snapshot, event: TimeLine) => {
    snapshot.node = {
      id: event.targetId,
      title: event.payload.title,
      description: event.payload.description || '',
      createdAt: event.timestamp,
      clusters: [],
      stats: {
        clusters: 0,
      },
    }

    snapshot.setIndex(event.targetId, [0])

    return snapshot
  },
  [TimeLineAction.NodeUpdated]: (snapshot: Snapshot, event: TimeLine) => {
    snapshot.updateNode((original) => {
      return {
        ...original,
        title: event.payload.title,
        description: event.payload.description || '',
        isPrivate: event.payload.isPrivate,
      }
    })
    return snapshot
  },
  [TimeLineAction.ClusterCreated]: (snapshot: Snapshot, event: TimeLine) => {
    // snapshot.clusters.push(event.payload)
    if (!snapshot.existNode(event.tree[0]?.id || '')) {
      return snapshot
    }
    const cluster = {
      id: event.payload.clusterId || '',
      topic: event.payload.title,
      perspectives: [] as SnapshotPerspective[],
      createdAt: event.timestamp,
      nodeId: event.targetId || '',
      stats: {
        perspectives: 0,
        contributions: 0,
        votesCount: 0,
      },
      contentUpdatedAt: event.timestamp,
    } as SnapshotCluster
    snapshot.updateNode((original) => {
      return {
        ...original,
        stats: {
          ...original.stats,
          clusters: original.stats.clusters + 1,
        },
        clusters: [...original.clusters, cluster],
      }
    })

    snapshot.setIndex(cluster.id, [0, snapshot.node.clusters.length - 1])

    return snapshot
  },
  [TimeLineAction.PerspectiveCreated]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const clusterId = event.tree[1]?.id || ''
    if (!snapshot.existNode(clusterId)) {
      return snapshot
    }

    if (snapshot.existNode(event.payload.id || '')) {
      return snapshot
    }
    const perspective = {
      id: event.payload.articleId || '',
      title: event.payload.title,
      contributions: [],
      minCoRank: event.payload.minCoRank || undefined,
      votes: [],
      ownerId: event.userId || '',
      votesCount: 0,
      nodeId: snapshot.node.id,
      clusterId: clusterId,
      cluster: {
        id: clusterId,
        topic:
          snapshot.node.clusters.find((cluster) => cluster.id === clusterId)
            ?.topic || '',
      },
      content: event.payload.content || '',
      stats: {
        inquiries: 0,
        challenges: 0,
        edits: 0,
        ownerEdits: 0,
      },
    } as SnapshotPerspective

    snapshot.updateCluster(clusterId, (original) => {
      return {
        stats: {
          ...original.stats,
          perspectives: original.stats.perspectives + 1,
        },
        perspectives: [...original.perspectives, perspective],
        contentUpdatedAt: new Date(),
      }
    })

    const cluster = snapshot.getNode(clusterId) as SnapshotCluster
    snapshot.setIndex(perspective.id, [
      ...(snapshot.getPath(clusterId) || []),
      cluster.perspectives.length - 1,
    ])
    return snapshot
  },
  [TimeLineAction.PerspectiveUpdated]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const perspective = snapshot.getNode<SnapshotPerspective>(
      event.payload.articleId || ''
    )
    if (!perspective) {
      return snapshot
    }

    snapshot.updatePerspective(perspective, (original) => {
      return {
        title:
          event.payload.title !== ''
            ? event.payload.title
            : original.title || '',
        content: event.payload.content || original.content || '',
        minCoRank: event.payload.minCoRank || original.minCoRank || undefined,
      }
    })

    if (perspective.content !== event.payload.content) {
      snapshot.updateCluster(perspective.clusterId, () => {
        return {
          contentUpdatedAt: new Date(),
        }
      })
    }

    return snapshot
  },
  [TimeLineAction.EditContributionCreated]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    return createContribution(snapshot, event, ContributionType.Edit)
  },
  [TimeLineAction.ChallengeContributionCreated]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    return createContribution(snapshot, event, ContributionType.Challenge)
  },
  [TimeLineAction.OwnerEditContributionCreated]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    return createContribution(snapshot, event, ContributionType.OwnerEdit)
  },
  [TimeLineAction.ContributionApproved]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const contribution = snapshot.getNode<SnapshotContribution>(event.targetId)
    if (!contribution) {
      return snapshot
    }

    // Avoid duplicated
    if (contribution.status === ContributionStatus.Accepted) {
      return snapshot
    }
    snapshot.updateContribution(contribution, () => {
      return { status: ContributionStatus.Accepted, response: event.title }
    })

    snapshot.updateCluster(contribution.clusterId, () => {
      return {
        contentUpdatedAt: new Date(),
      }
    })
    // Accepted don't reduce the stats
    // reduceContributionStats(snapshot, contribution)
    return snapshot
  },
  [TimeLineAction.ContributionBumpedUp]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    return createBump(snapshot, event)
  },
  [TimeLineAction.ContributionBumpCancelled]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const contribution = snapshot.getNode<SnapshotContribution>(event.targetId)
    if (!contribution) {
      return snapshot
    }

    snapshot.updateContribution(contribution, () => {
      return {
        bumps: contribution.bumps.filter(
          (bump) => bump.reason !== event.payload.title
        ),
        bumpCount: contribution.bumpCount - 1,
      }
    })
    return snapshot
  },
  [TimeLineAction.ContributionBumpedDown]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    return createBump(snapshot, event)
  },
  [TimeLineAction.ContributionCancelled]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const contribution = snapshot.getNode<SnapshotContribution>(event.targetId)
    if (!contribution) {
      return snapshot
    }
    // Avoid duplicated
    if (contribution.status === ContributionStatus.Cancelled) {
      return snapshot
    }

    snapshot.updateContribution(contribution, () => {
      return { status: ContributionStatus.Cancelled }
    })
    reduceContributionStats(snapshot, contribution)

    return snapshot
  },
  [TimeLineAction.ContributionCensured]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const contribution = snapshot.getNode<SnapshotContribution>(event.targetId)
    if (!contribution) {
      return snapshot
    }
    if (contribution.status === ContributionStatus.Censured) {
      return snapshot
    }

    snapshot.updateContribution(contribution, () => {
      return { status: ContributionStatus.Censured, reason: event.title }
    })

    reduceContributionStats(snapshot, contribution)
    return snapshot
  },
  [TimeLineAction.ContributionRejected]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const contribution = snapshot.getNode<SnapshotContribution>(event.targetId)
    if (!contribution) {
      return snapshot
    }
    // Avoid duplicated
    if (contribution.status === ContributionStatus.Rejected) {
      return snapshot
    }

    snapshot.updateContribution(contribution, () => {
      return {
        status: ContributionStatus.Rejected,
        reason: event.payload.reason || '',
      }
    })
    reduceContributionStats(snapshot, contribution)
    return snapshot
  },
  [TimeLineAction.InquiryContributionCreated]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    return createContribution(snapshot, event, ContributionType.Inquiry)
  },
  [TimeLineAction.PerspectiveDeleted]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const article = snapshot.getNode<SnapshotPerspective>(
      event.payload.id || ''
    )
    if (!article) {
      return snapshot
    }

    snapshot.updateCluster(article.clusterId, (original) => {
      return {
        perspectives: original.perspectives.filter(
          (perspective) => perspective.id !== event.payload.id
        ),
        stats: {
          ...original.stats,
          perspectives: original.stats.perspectives - 1,
          contributions:
            original.stats.contributions - article.contributions.length,
        },
      }
    })

    return snapshot
  },
  [TimeLineAction.PerspectiveTransferred]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const perspective = snapshot.getNode<SnapshotPerspective>(event.targetId)
    if (!perspective) {
      return snapshot
    }
    snapshot.updatePerspective(perspective, () => {
      return { ownerId: event.payload.newOwnerId || '' }
    })

    return snapshot
  },
  [TimeLineAction.PerspectiveVote]: (snapshot: Snapshot, event: TimeLine) => {
    const articleId = event.payload.articleId || ''
    if (!snapshot.existNode(articleId)) {
      return snapshot
    }

    const perspective = snapshot.getNode(articleId) as SnapshotPerspective

    // Avoid duplicates
    if (perspective.votes.find((vote) => vote.id === event.payload.id)) {
      return snapshot
    }
    const vote = {
      id: event.payload.id,
      userId: event.userId || '',
      username: event.payload.username || '',
      articleId: articleId,
      justification: event.payload.title || '',
      removedAt: undefined,
    } as SnapshotVote

    snapshot.updateCluster(perspective.clusterId, (original) => {
      return {
        stats: {
          ...original.stats,
          votesCount: original.stats.votesCount + 1,
        },
      }
    })

    snapshot.updatePerspective(perspective, (original) => {
      return {
        votesCount: original.votesCount + 1,
        votes: [...original.votes, vote],
      }
    })

    return snapshot
  },
  [TimeLineAction.PerspectiveVoteRemoved]: (
    snapshot: Snapshot,
    event: TimeLine
  ) => {
    const articleId = event.payload.articleId || ''
    if (!snapshot.existNode(articleId)) {
      return snapshot
    }
    const perspective = snapshot.getNode(articleId) as SnapshotPerspective

    snapshot.updateCluster(perspective.clusterId, (original) => {
      return {
        stats: {
          ...original.stats,
          votesCount: original.stats.votesCount - 1,
        },
      }
    })

    snapshot.updatePerspective(perspective, (original) => {
      return {
        votesCount: original.votesCount - 1,
        votes: original.votes.filter((vote) => vote.id !== event.payload.id),
      }
    })

    return snapshot
  },
  // We are not recreating task in this version
  [TimeLineAction.TaskCreated]: (snapshot: Snapshot, event: TimeLine) => {
    return snapshot
  },
  [TimeLineAction.NodeDeleted]: (snapshot: Snapshot, event: TimeLine) => {
    snapshot.updateNode((original) => {
      return {
        ...original,
        id: original.id || '',
        title: 'Node Deleted',
        description: '',
        clusters: [],
        stats: {
          clusters: 0,
        },
      }
    })
    return snapshot
  },
  [TimeLineAction.ClusterDeleted]: (snapshot: Snapshot, event: TimeLine) => {
    const cluster = snapshot.getNode(
      event.payload.clusterId || ''
    ) as SnapshotCluster
    if (!cluster) {
      return snapshot
    }
    // snapshot.clusters.push(event.payload)
    snapshot.updateNode((original) => {
      return {
        clusters: original.clusters.filter(
          (cluster) => cluster.id !== event.payload.clusterId
        ),
        stats: {
          ...original.stats,
          clusters: original.stats.clusters - 1,
        },
      }
    })
    return snapshot
  },
}

export class SnapshotFactory {
  public static increase(snapshotProps: SnapShotProps, event: TimeLine) {
    const snapshot = new Snapshot(snapshotProps)
    if (!FactoryMethods[event.action]) {
      console.warn('Factory method not found', event)
      return snapshot.toObject()
    }
    const newSnapshot = FactoryMethods[event.action](snapshot, event)
    newSnapshot.node = { ...snapshot.node }
    newSnapshot.tick = snapshot.tick + 1
    return newSnapshot.toObject()
  }
}
